import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { PRELOADER } from "@/constants/testIds";

const easing = [0.83, 0, 0.17, 1];

export default function Preloader() {
  const [show, setShow] = useState(() => {
    if (typeof window === "undefined") return false;
    return !sessionStorage.getItem("lak-preloaded");
  });
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    if (!show) return;
    const t1 = setTimeout(() => setRevealed(true), 1400);
    const t2 = setTimeout(() => {
      sessionStorage.setItem("lak-preloaded", "1");
      setShow(false);
    }, 2600);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [show]);

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          data-testid={PRELOADER.root}
          className="fixed inset-0 z-[100] pointer-events-none overflow-hidden"
          initial={{ opacity: 1 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0, transition: { duration: 0.4 } }}
        >
          {/* Two curtains, double staircase */}
          <motion.div
            className="curtain absolute top-0 left-0 w-1/2 h-full"
            style={{ background: "#FAF9F6" }}
            initial={{ y: 0 }}
            animate={{ y: revealed ? "-100%" : 0 }}
            transition={{ duration: 1.2, ease: easing, delay: revealed ? 0.05 : 0 }}
          />
          <motion.div
            className="curtain absolute top-0 right-0 w-1/2 h-full"
            style={{ background: "#FAF9F6" }}
            initial={{ y: 0 }}
            animate={{ y: revealed ? "100%" : 0 }}
            transition={{ duration: 1.2, ease: easing, delay: revealed ? 0.15 : 0 }}
          />
          <motion.div
            className="curtain absolute top-0 left-0 w-1/2 h-full"
            style={{ background: "#F2EFE8" }}
            initial={{ y: 0 }}
            animate={{ y: revealed ? "-100%" : 0 }}
            transition={{ duration: 1.2, ease: easing, delay: revealed ? 0.2 : 0 }}
          />
          <motion.div
            className="curtain absolute top-0 right-0 w-1/2 h-full"
            style={{ background: "#F2EFE8" }}
            initial={{ y: 0 }}
            animate={{ y: revealed ? "100%" : 0 }}
            transition={{ duration: 1.2, ease: easing, delay: revealed ? 0.3 : 0 }}
          />

          {/* Brand mark, perfectly centred on every screen */}
          <div className="absolute inset-0 flex flex-col items-center justify-center text-lak-text px-6 text-center">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: revealed ? 0 : 1, y: revealed ? -16 : 0 }}
              transition={{ duration: 0.9, ease: easing }}
              className="flex flex-col items-center max-w-[88vw]"
            >
              <span className="eyebrow mb-5 sm:mb-6">L &middot; A &middot; K &middot; Atelier</span>
              <motion.span
                initial={{ letterSpacing: "0.04em" }}
                animate={{ letterSpacing: "-0.01em" }}
                transition={{ duration: 1.6, ease: easing }}
                className="font-serif italic font-light leading-[1.02]
                           text-[12vw] sm:text-6xl md:text-7xl"
              >
                Thoughtfully designed.
              </motion.span>
              <span className="font-serif tracking-wide mt-2 text-lak-sage text-[5vw] sm:text-2xl md:text-3xl">
                Strategically built.
              </span>
              {/* tiny progress line */}
              <motion.div
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ duration: 1.4, ease: "linear" }}
                className="origin-left mt-8 sm:mt-10 h-px w-32 sm:w-40 bg-lak-sage/60"
              />
            </motion.div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
